import {AlertTriangle, X} from "lucide-react";
import {useEffect, useId, useRef} from "react";

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  busy: boolean;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  busy,
  destructive = false,
  onCancel,
  onConfirm
}: ConfirmDialogProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const cancelButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelButton.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [busy, onCancel, open]);

  if (!open) return null;
  return (
    <div className="confirm-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onCancel();
    }}>
      <section className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId}>
        <div className="confirm-symbol"><AlertTriangle size={25} strokeWidth={1.7} /></div>
        <button className="confirm-close" type="button" onClick={onCancel} disabled={busy} aria-label="关闭确认框"><X size={19} /></button>
        <p className="eyebrow">CONFIRM ACTION</p>
        <h2 id={titleId}>{title}</h2>
        <p id={descriptionId}>{description}</p>
        <div className="confirm-actions">
          <button ref={cancelButton} className="button button-ghost" type="button" onClick={onCancel} disabled={busy}>取消</button>
          <button className={`button ${destructive ? "button-danger" : "button-primary"}`} type="button" onClick={onConfirm} disabled={busy}>
            {busy ? "正在处理…" : confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
};
